import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { consume, ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { css, html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { api } from '../api.js';
import { contexts } from '../contexts.js';
import { githubMarkdownCSS } from '../githubMarkdownCSS.js';
import { docLinkStyles } from './cssstyles.js';
import { RepositoryItem } from './pick-repository.js';

export type GithubInstallation = {
  owner: string,
  repo: string,
}

function toItem(repo: WireTypes.GithubRepository): RepositoryItem {
  return {
    id: `${repo.owner}/${repo.name}`,
    prefix: repo.owner + '/',
    name: repo.name,
    avatarUrl: repo.avatarUrl,
    avatarShape: repo.ownerType === 'Organization' ? 'rounded' : 'circle',
    isPrivate: repo.private,
  };
}

@customElement('pick-github-installation')
class PickGithubInstallation extends LitElement {
  @consume({ context: contexts.serverInfo, subscribe: true }) private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  private _githubInstallations = new Task(this, {
    args: () => [],
    task: async ([], { signal }) => {
      return await api.projectSources.listMyGithubRepos.GET(undefined, { signal });
    }
  });

  private _select(repos: WireTypes.GithubRepository[], event: CustomEvent<RepositoryItem>) {
    const repo = repos.find(candidate => toItem(candidate).id === event.detail.id);
    if (!repo)
      return;
    this.dispatchEvent(new CustomEvent<GithubInstallation>('github-installation', {
      detail: {
        owner: repo.owner,
        repo: repo.name,
      }
    }));
  }

  override render() {
    const githubAppUrl = this._serverInfo?.githubAppPublicURL;
    if (!githubAppUrl)
      return nothing;

    const appInstallUrl = githubAppUrl.replace(/\/$/, '') + '/installations/new';

    return this._githubInstallations.render({
      pending: () => html`
        <h-box><sl-spinner></sl-spinner> Loading repositories...</h-box>
      `,
      error: () => html`
        <h-box>Something went wrong; please try again later.</h-box>
      `,
      complete: ({ needsReauth, repos }) => needsReauth ? html`
        <sl-button variant=primary href=${`/login/github?return_to=${encodeURIComponent(window.location.href)}`}>
          Please re-login to GitHub
        </sl-button>
      ` : repos.length === 0 ? html`
        <sl-button variant=primary href=${appInstallUrl} target=_blank>
          Install Flakiness.io app <sl-icon slot=suffix library=boxicons name=bx-link-external></sl-icon>
        </sl-button>
      ` : html`
        <v-box>
          <pick-repository
            .items=${repos.map(toItem)}
            .icon=${'github'}
            .placeholder=${'Choose your GitHub repository'}
            @repository-picked=${this._select.bind(this, repos)}
          ></pick-repository>
          <div>
            Can't see your repository? <a-ext href=${appInstallUrl}>Configure Flakiness.io app</a-ext>
          </div>
        </v-box>
      `,
    });
  }

  static styles = [docLinkStyles, githubMarkdownCSS, css`
    :host {
      display: block;
    }

    .loading-message {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: var(--sl-spacing-large);
      gap: var(--sl-spacing-medium);
    }

    a-ext {
      color: var(--sl-color-primary-600);
    }
  `];
}
